import { Plus, Check, Goal, Trophy } from "lucide-react";
import { toast } from "sonner";
import { useSlip } from "../context/SlipContext";
import { fmtOdds } from "../lib/api";
import { TeamLogo } from "./TeamLogo";

export const GoalscorerCard = ({ prop }) => {
  const { addRawLeg, setOpen, hasLeg } = useSlip();
  const added = hasLeg(prop.id);
  const pct = Math.max(0, Math.min(100, prop.yes_pct ?? 0));
  const tc = prop.color || "#233248";
  const isTD = prop.market_type === "anytime_td";

  const add = () => {
    addRawLeg({
      prop_id: prop.id,
      player_name: prop.player_name,
      metric: prop.metric,
      line: "",
      lean: isTD ? "To Score TD" : "To Score",
      odds: prop.yes_odds,
      team_abbr: prop.team_abbr,
      opponent_abbr: prop.opponent_abbr,
      game_label: prop.game_label || null,
      sport: prop.sport,
    });
    setOpen(true);
    toast.success(`Added ${prop.player_name} · ${prop.metric}`);
  };

  return (
    <div
      data-testid={`goalscorer-card-${prop.id}`}
      className="rounded-xl border border-[#233248] p-3 flex items-center gap-3"
      style={{ background: `linear-gradient(135deg, ${tc}26 0%, #141C2B 55%)`, boxShadow: `inset 3px 0 0 ${tc}` }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <TeamLogo sport={prop.sport} abbr={prop.team_abbr} color={tc} size="sm" />
          <span className="text-white font-semibold text-sm truncate">{prop.player_name}</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <div className="h-1.5 w-20 rounded-full bg-[#233248] overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#10B981" }} />
          </div>
          <span className="font-data text-[11px] text-slate-400">{pct}% implied</span>
        </div>
      </div>
      <button
        data-testid={`goalscorer-add-${prop.id}`}
        onClick={add}
        className="shrink-0 flex items-center gap-1.5 rounded-lg border border-[#233248] bg-[#0B0F17] px-3 py-2 hover:border-[#10B981] transition-colors"
        title="Add to slip"
      >
        <span className="font-data text-sm font-bold text-white">{fmtOdds(prop.yes_odds)}</span>
        {added ? <Check className="h-4 w-4 text-[#10B981]" /> : <Plus className="h-4 w-4 text-[#00E5FF]" />}
      </button>
    </div>
  );
};

export const GoalscorerSection = ({ props }) => {
  if (!props || props.length === 0) return null;
  const isTD = props.some((p) => p.market_type === "anytime_td");
  const groups = [
    { key: "anytime_td", label: "Anytime Touchdown Scorer" },
    { key: "anytime", label: "Anytime Goalscorer" },
    { key: "first", label: "First Goalscorer" },
  ].map((g) => ({
    ...g,
    rows: props.filter((p) => p.market_type === g.key).sort((a, b) => (b.yes_pct || 0) - (a.yes_pct || 0)),
  })).filter((g) => g.rows.length > 0);

  return (
    <div data-testid="goalscorer-section" className="mt-6">
      <h3 className="flex items-center gap-2 font-heading font-bold text-white text-sm mb-3">
        {isTD ? <Trophy className="h-4 w-4 text-[#10B981]" /> : <Goal className="h-4 w-4 text-[#10B981]" />}
        {isTD ? "Touchdown Scorer" : "Goalscorer Markets"}
        <span className="font-data text-[11px] text-slate-500">
          {isTD ? "who the books fancy to find the end zone" : "who the books fancy to score"}
        </span>
      </h3>
      <div className="space-y-5">
        {groups.map((g) => (
          <div key={g.key} data-testid={`goalscorer-group-${g.key}`}>
            <div className="text-[11px] font-data uppercase tracking-wide text-slate-400 mb-2">{g.label}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {g.rows.map((p) => (
                <GoalscorerCard key={p.id} prop={p} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
